import { useEffect, useMemo, useRef } from 'react';

export type DebouncedSourceCallback<TReturn> = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ...args: any[]
) => TReturn | Promise<TReturn>;

export type DebouncedCallback<T extends DebouncedSourceCallback<ReturnType<T>>> =
    DebouncedSourceCallback<Promise<ReturnType<T>>> & {
        cancel: () => void;
        flush: () => void;
    };

/**
 * Returns a function that can be called multiple times, but
 * will only execute after the specified debounceTime.  The
 * most recent version of the callback will be executed.
 *
 * @param debounceTime The debounce time in milliseconds.
 * @param callback The function to debounce.
 * @returns A debounced function with a cancel method.
 */
export function useDebounceCallback<T extends DebouncedSourceCallback<ReturnType<T>>>(
    debounceTime: number,
    callback: T,
): DebouncedCallback<T> {
    const timeoutRef = useRef<number | null>(null);
    const currentCallback = useRef(callback);
    currentCallback.current = callback;

    const pendingArgsRef = useRef<unknown[] | null>(null);

    const debouncedCallback = useMemo(() => {
        const cancel = () => {
            if (timeoutRef.current !== null) {
                window.clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            pendingArgsRef.current = null;
        };

        const flush = () => {
            if (timeoutRef.current !== null) {
                const args = pendingArgsRef.current ?? [];
                cancel();
                currentCallback.current(...args);
            }
        };

        const fn = (...args: unknown[]) => {
            cancel();
            pendingArgsRef.current = args;
            return new Promise<ReturnType<T>>((resolve, reject) => {
                timeoutRef.current = window.setTimeout(() => {
                    try {
                        resolve(currentCallback.current(...args));
                    } catch (err) {
                        reject(err);
                    }
                    timeoutRef.current = null;
                    pendingArgsRef.current = null;
                }, debounceTime);
            });
        };
        fn.cancel = cancel;
        fn.flush = flush;

        return fn;
    }, [debounceTime]);

    // Cancel the callback if the parent component is unmounted
    useEffect(() => debouncedCallback.cancel, [debounceTime, debouncedCallback]);

    return debouncedCallback;
}
